import {
  Edit,
  SimpleForm,
  Toolbar,
  TextInput,
  NumberInput,
  SelectInput,
  required,
  DateInput,
  SaveButton,
  ShowButton,
  useGetOne,
} from "react-admin";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
  Grid,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ComponentEdit from "../../components/PageComponents/ComponentEdit";
import FormField from "../../components/Common/FormField";
import { useCurrentProject } from "../../contexts/AccountProjectContext";
import { useSyncProjectIdToForm } from "../../hooks/useSyncProjectIdToForm";
import { paymentCurrencyChoices } from "../../models/paymentCurrency";

const PaymentLinkFields = () => {
  const { t }: { t: any } = useTranslation();

  return (
    <>
      <TextInput
        source="type"
        defaultValue="cryptoOrder"
        style={{ display: 'none' }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormField label={t("Internal reference")}>
            <TextInput
              source="internalRef"
              label={t("Internal reference")}
              fullWidth
              validate={required()}
              helperText={t("A unique identifier for this payment link")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Amount (EUR)")}>
            <NumberInput
              source="euroAmount"
              label={t("Amount (EUR)")}
              fullWidth
              validate={required()}
              helperText={t("The amount to be paid in euros")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Payment currency")}>
            <SelectInput
              source="currency"
              label={t("Payment currency")}
              fullWidth
              validate={required()}
              choices={[
                ...paymentCurrencyChoices,
                { id: "customer_choice", name: "Let customer choose" },
              ]}
              helperText={t("The cryptocurrency the customer pays in")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Expiration date")}>
            <DateInput
              source="expiresAt"
              label={t("Expiration date")}
              fullWidth
              validate={required()}
              helperText={t("The payment link expires on this date")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Customer name")}>
            <TextInput
              source="customerName"
              label={t("Customer name")}
              fullWidth
              validate={required()}
              helperText={t("Name of the customer who receives the payment link")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Customer email")}>
            <TextInput
              source="customerEmail"
              label={t("Customer email")}
              fullWidth
              validate={required()}
              type="email"
              helperText={t("Email address the payment link is sent to")}
            />
          </FormField>
        </Grid>
      </Grid>
    </>
  );
};

export const PaymentLinkEdit = () => {
  const { t }: { t: any } = useTranslation();
  const { id } = useParams();
  const { data: record } = useGetOne("paymentLinks", { id }, { enabled: Boolean(id) });
  const { typeProject } = useCurrentProject();
  const projectId = useSyncProjectIdToForm();

  const validateCryptoOrder = (values: any) => {
    const errors: any = {};

    if (typeProject !== "cryptoPayment") {
      errors.projectId =
        "Entity selection is not configured to create a crypto payment. Please select another entity on top of the page or update the configuration in the entity settings.";
    }

    if (values.sendLink === "yes" && !values.message) {
      errors.message = "Message is required when sending link to customer";
    }

    return errors;
  };

  const transformData = async (data: any) => {
    return {
      ...data,
      projectId,
    };
  };

  return (
    <ComponentEdit
      title="Edit payment link"
      subtitle={record?.internalRef ? `Ref. ${record.internalRef}` : "Update the payment link."}
    >
      <Card>
        <Box
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">
              {t("Payment link details")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("Change the amount, currency, expiration date or customer of this payment link.")}
            </Typography>
          </Box>
          {record && <ShowButton record={record} resource="paymentLinks" icon={<VisibilityOutlinedIcon />} />}
        </Box>
        <Divider />
        <CardContent
          sx={{
            p: 4,
          }}
        >
          <Edit redirect="show" transform={transformData} actions={false}>
            <SimpleForm
              validate={validateCryptoOrder}
              toolbar={<Toolbar sx={{ m: 2 }}><SaveButton label={t("Save")} /></Toolbar>}
            >
              <PaymentLinkFields />
            </SimpleForm>
          </Edit>
        </CardContent>
      </Card>
    </ComponentEdit>
  );
};

export default PaymentLinkEdit;
